// Customer "Your Kitchen Journey" autopilot demo (8 steps).
//
// Pure scripted overlay via @tkos/demo-engine — simulated cards/messages on top
// of the real customer portal. No API calls, no payments, no real data touched.
// Triggered by ?demo=customer.

import { startDemo, type DemoStep, type DemoHandle } from "@tkos/demo-engine";

const AI_WIDGET = "#cust-ai-widget";

const STEPS: DemoStep[] = [
  {
    name: "Aria on the website",
    narration: "A visitor lands on the retailer's website at 9pm.",
    run: (d) => {
      d.showMessage({ side: "in", from: "Aria · Turner Kitchens", text: "Hi! I'm Aria from Turner Kitchens. Thinking about a new kitchen? I can help you get started. 😊" });
    },
  },
  {
    name: "Aria qualifies",
    narration: "Aria asks the right questions. No forms. Just conversation.",
    durationMs: 8000,
    run: async (d) => {
      d.showMessage({ side: "out", from: "You", text: "Looking for a modern kitchen, around £15k, ready in 3 months." });
      await d.wait(1100);
      d.showMessage({ side: "in", from: "Aria", text: "Lovely! We have some beautiful modern ranges. What size is the space?" });
    },
  },
  {
    name: "Handed to wizard",
    narration: "When ready, Aria hands them to the wizard — pre-filled.",
    run: async (d) => {
      d.showMessage({ side: "in", from: "Aria", text: "I've got everything I need — let me show you your options." });
      await d.wait(900);
      d.showCard(`<div style="background:#E8A020;color:#1a1206;font-weight:800;border-radius:10px;padding:.6rem;text-align:center">See my personalised options →</div>
        <div style="font-size:.78rem;color:#6b7280;margin-top:6px;text-align:center">Opens the wizard with name, budget &amp; style pre-filled.</div>`);
    },
  },
  {
    name: "Enquiry submitted",
    narration: "Enquiry submitted. Customer receives instant confirmation.",
    run: (d) => {
      d.showCard(`<div style="font-weight:800">Thanks — your enquiry is in! ✅</div>
        <div style="font-size:.84rem;color:#6b7280;margin-top:4px">Confirmation email sent. Here's a link to your personal project portal.</div>`);
    },
  },
  {
    name: "Customer portal",
    narration: "A professional portal. Branded to Turner Kitchens. Aria ready to help.",
    run: (d) => {
      d.highlight(AI_WIDGET, { label: "Aria" });
      d.showCard(`<div style="font-weight:800">Your kitchen project</div>
        <div style="font-size:.84rem;color:#6b7280;margin-top:4px">Quote: pending · Timeline shown · Aria available bottom-right, 24/7.</div>`);
    },
  },
  {
    name: "Quote arrives",
    narration: "Quote arrives. Aria helps them understand it.",
    run: async (d) => {
      d.toast("🔔 Your quote is ready");
      await d.wait(900);
      d.showMessage({ side: "in", from: "Aria", text: "Your quote is £14,200 — shall I walk you through it?" });
    },
  },
  {
    name: "Customer accepts and pays",
    narration: "One click to accept. Secure payment through the portal.",
    run: async (d) => {
      d.showCard(`<div style="font-weight:800">Accept quote · Pay deposit</div>
        <div style="font-size:.84rem;color:#6b7280;margin-top:4px">Deposit £4,260 · secure card payment.</div>`);
      await d.wait(1100);
      d.showCard(`<div style="font-weight:800;color:#16a34a">Payment confirmed ✅</div>
        <div style="font-size:.84rem;color:#6b7280;margin-top:4px">Your deposit is in — your kitchen is on its way.</div>`);
    },
  },
  {
    name: "Aria looks after them",
    narration: "Aria is there throughout the whole journey. 24/7.",
    durationMs: 7000,
    run: async (d) => {
      d.highlight(AI_WIDGET, { label: "Aria" });
      d.showMessage({ side: "out", from: "You", text: "When is my delivery?" });
      await d.wait(1000);
      d.showMessage({ side: "in", from: "Aria", text: "Your kitchen is scheduled for delivery on Monday 21st July. We'll confirm a time the week before. 🚚" });
    },
  },
];

let handle: DemoHandle | null = null;

/** Start the customer demo if the URL says so (?demo=customer). */
export function maybeStartCustomerDemo(): void {
  try {
    if (new URLSearchParams(window.location.search).get("demo") !== "customer") return;
  } catch { return; }
  if (handle) return;
  window.setTimeout(() => { handle = startDemo({ title: "Customer Demo — Your Kitchen Journey", steps: STEPS }); }, 600);
}
